import { useState, useEffect } from "react";
import { useParams } from "next/navigation";
import { Users } from "@platzi/icons";
import usePermissionStore from "@/stores/usePermissionStore";
import useCouponsStudents from "@/stores/useCouponsStudents";
import { Button } from "@/components/shared";
import * as Dialog from "@radix-ui/react-dialog";
import { getIsStudentActive, createStudents, assignPermission } from "@/api/dashboard";
import { ModalInput } from "./ModalInput";
import styles from "./ModalContent.module.sass";

interface ModalContentProps {
  onSubmit: (value: boolean) => void;
}

export const ModalContent = ({ onSubmit }: ModalContentProps) => {
  const setNewAdmin = usePermissionStore((state) => state.updateAdmins);
  const urlAfterActivatingAdmin = usePermissionStore((state) => state.urlAfterActivatingAdmin);
  const companyId = useCouponsStudents((state) => state.companyId);
  const couponsPermission = useCouponsStudents((state) => state.couponsPermission);

  const [tag, setTag] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [basePath, setBasePath] = useState("");

  const params = useParams();
  const paramCompanyId = couponsPermission ? companyId : params?.companyId;

  useEffect(() => {
    setBasePath(window.location.origin);
  }, []);

  const createAdmin = async () => {
    const formattedData = {
      students: [
        {
          email: tag,
          renew_months: 0,
          is_admin: true,
          next_url: basePath + urlAfterActivatingAdmin,
        },
      ],
    };
    const newAdmin = await createStudents(paramCompanyId, formattedData);
    setNewAdmin(newAdmin[0]);
    onSubmit(false);
  };

  const assignAdminPermissions = async (student: StudentLicenseInfo) => {
    const formattedData = {
      company_user_ids: [student.company_user_id],
    };
    await assignPermission(paramCompanyId, formattedData);
    setNewAdmin(student);
    onSubmit(false);
  };

  const handleSubmitAdmins = async () => {
    if (isLoading) return null;
    if (!tag) return null;

    setIsLoading(true);
    const emailEncoded = encodeURIComponent(tag);
    const isStudentActive = await getIsStudentActive(paramCompanyId, emailEncoded);

    if (isStudentActive?.length > 0) {
      await assignAdminPermissions(isStudentActive[0]);
    } else {
      await createAdmin();
    }
  };

  return (
    <div className={styles.ModalContent}>
      <div className={styles.ModalContent_icon}>
        <Users />
      </div>
      <h3>Asigna un nuevo administrador</h3>
      <p>Agrega una persona para administrar esta cuenta de Platzi for Business.</p>
      <ModalInput tag={tag} setTag={setTag} couponsPermission={couponsPermission} />
      <div className={styles.ModalPermissions}>
        <b>Permisos de edición que otorgarás:</b>
        <p>
          <b>Reportes</b> Visualizar y descargar reportes
        </p>
        {!couponsPermission && (
          <>
            <p>
              <b>Licencias</b> Asignar y quitar licencias
            </p>
            <p>
              <b>Equipos</b> Crear, asignar y eliminar equipos
            </p>
            <p>
              <b>Rutas</b> Crear, asignar y eliminar rutas de aprendizaje
            </p>
            <p>
              <b>Permisos</b> Agregar y eliminar administradores
            </p>
          </>
        )}
      </div>
      <div className={styles.ModalActions}>
        <Button size="lg" onClick={handleSubmitAdmins} dataQa="send-invitations-btn" isLoading={isLoading}>
          Asignar Administrador
        </Button>
        <Dialog.Close asChild>
          <Button size="lg" variant="neutral" dataQa="cancel-invitations-btn">
            Cancelar
          </Button>
        </Dialog.Close>
      </div>
    </div>
  );
};
